import { superstructResolver } from '@hookform/resolvers/superstruct';
import { Controller } from 'react-hook-form';

import Form from '@/components/content/form/Form';
import MercuryTextInput from '@/components/content/form/components/TextInput';
import MercuryFile from '@/components/content/form/components/File';
import MercuryTextArea from '@/components/content/form/components/TextArea';
import MercuryNativeSelect from '@/components/content/form/components/NativeSelect';
import InputDisplayControl from '@/components/content/form/components/InputDisplayControl';
import SingleSelect from '@/components/content/combobox/SingleSelect';

import { useSupportForm } from './useSupportForm';
import { useMSSP } from '@/hooks/useIsMSSP';
import { useAuth } from '@/hooks/useAuth';
import { useConfirmation } from '@/hooks/useConfirmation';
import { getEntries } from '@/utils';
import { URGENT_DEADLINE_DIALOG, earliestDeadline, isUrgentDeadline } from './utils/urgentDeadline';

import { type SupportFormFields, SupportFormLabels } from './types';
import type { FetchError } from '@/types/api';
import type { OnSubmitFunc } from '@/types/form';
import MercuryDateInput from '@/components/content/form/components/MercuryDateInput';

import classes from './SupportForm.module.css';

interface FormProps {
  prefillData?: Partial<SupportFormFields>;
  attachment?: boolean;
  subjects: Array<string>;
  name: string;
  disabledFields?: { [key in keyof SupportFormFields]?: boolean };
  hideFields?: { [key in keyof SupportFormFields]?: boolean };
  onSuccess?: VoidFunction;
  onError?: (error: FetchError) => void;
  triggerOtherOn?: string;
  relationship?: string;
  extraDescription?: {
    value: string;
    label: string;
    triggerOn: string;
  };
}

const SupportForm = ({
  prefillData,
  attachment,
  disabledFields,
  subjects,
  hideFields,
  onSuccess,
  onError,
  name,
  triggerOtherOn,
  relationship,
  extraDescription,
}: FormProps) => {
  const { onSubmit, isPostingRfi, schema } = useSupportForm({
    onSuccess,
    onError,
    type: prefillData?.to,
    relationship,
    extraDescription: extraDescription?.value,
  });

  const { isMSSP, subclientsObj } = useMSSP();

  const auth = useAuth();

  const { getConfirmation } = useConfirmation();

  const handleSubmit: OnSubmitFunc<SupportFormFields> = async (
    data,
    setGeneralError,
    setSuccessMessage,
    reset
  ) => {
    if (!hideFields?.due_date && isUrgentDeadline(data.due_date)) {
      const confirmed = await getConfirmation(URGENT_DEADLINE_DIALOG);
      if (!confirmed) return;
    }

    onSubmit(data, setGeneralError, setSuccessMessage, reset);
  };

  return (
    <Form<SupportFormFields>
      onSubmit={handleSubmit}
      options={{
        resolver: superstructResolver(schema),
        defaultValues: {
          subject: subjects[0],
          ...prefillData,
        },
      }}
      submitButton={{
        text: 'Submit',
        disabled: isPostingRfi,
      }}
      name={name}
      fields={SupportFormLabels}
    >
      {({ register, watch, control, formState: { errors }, resetField }) => (
        <>
          {!hideFields?.subclient_id && isMSSP && (
            <MercuryNativeSelect<SupportFormFields>
              register={register}
              name="subclient_id"
              data={[
                ['Myself', `Myself (${auth.tokenInfo.client_name})`],
                ...getEntries(subclientsObj),
              ].map(([value, label]) => ({ value: `${value}`, label }))}
              label={SupportFormLabels.subclient}
              error={errors?.subclient_id?.message}
              disabled={disabledFields?.subclient_id}
              required
            />
          )}

          {!hideFields?.subject && (
            <div className={classes.subjectSelect}>
              <Controller
                name="subject"
                control={control}
                render={({ field: { value, onChange } }) => (
                  <SingleSelect
                    items={subjects.map((subject) => ({ label: subject, value: subject }))}
                    value={value ? { label: value, value } : undefined}
                    setValue={(item) => onChange(item.value)}
                    label={SupportFormLabels.subject}
                    error={errors?.subject?.message}
                    disabled={disabledFields?.subject}
                    required
                  />
                )}
              />
            </div>
          )}

          {triggerOtherOn && !hideFields?.otherSubject && (
            <InputDisplayControl<SupportFormFields>
              watch={watch}
              watchFieldName="subject"
              watchValue={triggerOtherOn}
            >
              <MercuryTextInput<SupportFormFields>
                register={register}
                label={SupportFormLabels.otherSubject}
                type="text"
                name="otherSubject"
                error={errors?.otherSubject?.message}
                placeholder="Write a subject"
                disabled={disabledFields?.otherSubject}
                required
              />
            </InputDisplayControl>
          )}

          <MercuryDateInput<SupportFormFields>
            register={register}
            watch={watch}
            label={SupportFormLabels.dueDate}
            name="due_date"
            min={earliestDeadline()}
            error={errors?.due_date?.message}
            hidden={hideFields?.due_date}
            disabled={disabledFields?.due_date}
            required
            disableWeekends
            displayFormat="YYYY-MM-DD"
          />

          {extraDescription && extraDescription.triggerOn && (
            <InputDisplayControl<SupportFormFields>
              watch={watch}
              watchFieldName="subject"
              watchValue={extraDescription.triggerOn}
            >
              <MercuryTextInput<SupportFormFields>
                register={register}
                label={extraDescription.label}
                type="number"
                name="extraDescription"
                error={errors.extraDescription?.message}
                displayError
              />
            </InputDisplayControl>
          )}

          {!hideFields?.description && (
            <MercuryTextArea<SupportFormFields>
              register={register}
              name="description"
              label={SupportFormLabels.description}
              error={errors?.description?.message}
              displayError
              required
            />
          )}

          {attachment && (
            <MercuryFile<SupportFormFields>
              register={register}
              name="attachment"
              label={SupportFormLabels.attachment}
              watch={watch}
              resetField={resetField}
            />
          )}

          <MercuryTextInput register={register} type="hidden" name="to" hidden />

          <MercuryTextInput register={register} type="hidden" name="ticket_id" hidden />
        </>
      )}
    </Form>
  );
};

export default SupportForm;
